import { GButton, GDialog, GInput } from 'components/basic-blocks/'
import { ChangeEvent, useState } from 'react'
import { sanitizeURL, setItemsToLocalStorage } from 'utils'
import { useAccountStore, useAssetStore, useUiStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { Onboarding } from '../../services/user'
import { toast } from 'react-toastify'
import { useNavigateWithParams } from '../../hooks'

export const CreateWorkspaceDialog = () => {
	const navigate = useNavigateWithParams()
	const [createAccountForm, setCreateAccountForm] = useState<{
		domain: string
		spend: number | null
	}>({
		domain: '',
		spend: null
	})
	const [errors, setErrors] = useState<{
		domain: string | null
		spend: string | null
	}>({
		domain: null,
		spend: null
	})
	const [loading, setLoading] = useState(false)

	const { createWorkspaceDialogOpen, setCreateWorkspaceDialogOpen } =
		useUiStore(
			useShallow((s) => ({
				createWorkspaceDialogOpen: s.createWorkspaceDialogOpen,
				setCreateWorkspaceDialogOpen: s.setCreateWorkspaceDialogOpen
			}))
		)

	const { setAccount, getAllAccounts } = useAccountStore(
		useShallow((s) => ({
			setAccount: s.setAccount,
			getAllAccounts: s.getAllAccounts
		}))
	)

	const { setAsset, setDemoMode } = useAssetStore(
		useShallow((s) => ({
			setAsset: s.setAsset,
			setDemoMode: s.setDemoMode
		}))
	)

	const handleDialogClose = () => {
		setCreateWorkspaceDialogOpen(false)
		setCreateAccountForm({ domain: '', spend: null })
		setErrors({ domain: null, spend: null })
		setLoading(false)
	}

	const handleChange = (e: string, name: string) => {
		setCreateAccountForm({ ...createAccountForm, [name]: e })
	}

	const handleSubmit = async () => {
		let account
		setLoading(true)
		if (!createAccountForm.domain || !createAccountForm.spend) {
			setErrors({
				spend: !createAccountForm.spend ? 'Spend is required' : null,
				domain: !createAccountForm.domain ? 'Domain is required' : null
			})
			return setLoading(false)
		}
		try {
			account = await Onboarding.createProductAccount({
				type: 'standard',
				spend: Number(createAccountForm.spend || 0) * 1000,
				step: 'workspace',
				domain: createAccountForm.domain
			})
			setAccount(account)
			setAsset(null)
			setDemoMode(true)
			await getAllAccounts()
			setItemsToLocalStorage([
				{ key: 'account_id', value: account?.id },
				{ key: 'selected_asset', value: 'website-demo' }
			])
			setLoading(false)
			handleDialogClose()
			return navigate(`/start/standard/${account.id}`)
		} catch (error: any) {
			setLoading(false)
			toast.error(error.response.data.message || 'Something went wrong')
		}
	}

	return (
		<GDialog
			open={createWorkspaceDialogOpen}
			maxWidth="3xl"
			onClose={handleDialogClose}
			title="Tell us about your business"
			subtitle="We'll use this information to create your workspace and provide you with smooth integration."
		>
			<div className="flex items-center gap-6 flex-col py-6">
				<GInput
					label="What is your main business domain?"
					labelClassName="text-base text-color-text-default font-medium"
					className="flex justify-between flex-col gap-1 w-full"
					inputClassName="w-full placeholder:font-[300] text-base font-medium"
					errorPosition="flex"
					error={errors.domain || undefined}
					placeholder="example.com"
					value={sanitizeURL(createAccountForm.domain)}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						handleChange(e.target.value, 'domain')
					}
				/>
				<GInput
					label="What is your total monthly advertising spend? (USD)"
					labelClassName="text-base text-color-text-default font-medium"
					className="flex justify-between flex-col gap-1 w-full"
					inputClassName="w-full text-base font-medium"
					errorPosition="flex"
					error={errors.spend || undefined}
					placeholder="Amount"
					type="number"
					value={createAccountForm.spend || ''}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						handleChange(e.target.value, 'spend')
					}
				/>
				<GButton
					color="yellow"
					labelClassName="font-base font-medium leading-5"
					className="w-full rounded-full p-2 disabled:!shadow-none !shadow-s-yellow"
					iconPlacement="right"
					disabled={loading}
					onClick={handleSubmit}
					loader={loading}
				>
					<span>Continue</span>
				</GButton>
			</div>
		</GDialog>
	)
}
